// Système de rôle et permission — socle sans React (décisions du 17 au 22/09/2026).
// Trois niveaux : rôle → capacité (policy) → action (permission).
// Le catalogue et ses libellés viennent de la gateway (`GET /authz/catalogue`) : la webapp n'en garde
// aucune copie. L'`accessLevel` ne sert qu'à l'affichage (icône de ligne) et à la règle « grisé pour
// agir, caché pour détruire » — jamais à décider d'un droit.
import type { AuthzCatalogue } from '@/api'

export type AccessLevel =
  | 'read'
  | 'write'
  | 'delete'
  | 'execute'
  | 'permissions_management'

export type Module = 'declarations' | 'operations' | 'referentials' | 'settings'

export interface ActionDef {
  code: string
  module: Module
  accessLevel: AccessLevel
  label: string
}

export interface CapacityDef {
  id: string
  section: Module
  label: string
  description: string
  actions: readonly string[]
}

/** Les rôles Nabu du contrat (`Preset`) ; leur contenu et leurs libellés viennent du catalogue. */
const PRESET_IDS = ['guest', 'declarant', 'manager', 'admin'] as const

export type PresetId = (typeof PRESET_IDS)[number]

export interface PresetDef {
  id: PresetId
  rank: number
  label: string
  description: string
  capacities: readonly string[]
}

/** Le catalogue servi par la gateway, indexé une fois au chargement. */
export interface Catalogue {
  actions: readonly ActionDef[]
  capacities: readonly CapacityDef[]
  presets: readonly PresetDef[]
  /** Les modules, dans l'ordre du catalogue. */
  sections: readonly Module[]
  actionsByCode: ReadonlyMap<string, ActionDef>
  capacitiesById: ReadonlyMap<string, CapacityDef>
  presetsById: ReadonlyMap<PresetId, PresetDef>
  moduleLabels: ReadonlyMap<string, string>
  /** Un titre par préfixe d'action (`flux` pour `flux.create`). */
  resourceLabels: ReadonlyMap<string, string>
}

export type CatalogueLang = 'fr' | 'en' | 'de'

const LANGS: readonly CatalogueLang[] = ['fr', 'en', 'de']

/** La langue d'i18next ramenée à celles du catalogue ; le français sinon. */
export const catalogueLang = (language: string | undefined): CatalogueLang =>
  LANGS.find((lang) => lang === language?.slice(0, 2).toLowerCase()) ?? 'fr'

export const isPresetId = (value: string): value is PresetId =>
  (PRESET_IDS as readonly string[]).includes(value)

/** Un code sans libellé ne casse rien : l'écran affiche le code. */
const labelOr = (label: string, code: string): string => label.trim() || code

export const catalogueFrom = (response: AuthzCatalogue): Catalogue => {
  const actions: ActionDef[] = response.actions.map((action) => ({
    code: action.code,
    module: action.module as Module,
    accessLevel: action.accessLevel as AccessLevel,
    label: labelOr(action.label, action.code),
  }))
  const capacities: CapacityDef[] = response.capacities.map((capacity) => ({
    id: capacity.code,
    section: capacity.module as Module,
    label: labelOr(capacity.label, capacity.code),
    description: capacity.description,
    actions: capacity.actions,
  }))
  const presets: PresetDef[] = response.presets.flatMap((preset) =>
    isPresetId(preset.code)
      ? [
          {
            id: preset.code,
            rank: preset.rank,
            label: labelOr(preset.label, preset.code),
            description: preset.description,
            capacities: preset.capacities,
          },
        ]
      : []
  )
  return {
    actions,
    capacities,
    presets,
    sections: response.modules.map((module) => module.code as Module),
    actionsByCode: new Map(actions.map((x) => [x.code, x])),
    capacitiesById: new Map(capacities.map((x) => [x.id, x])),
    presetsById: new Map(presets.map((x) => [x.id, x])),
    moduleLabels: new Map(
      response.modules.map((x) => [x.code, labelOr(x.label, x.code)])
    ),
    resourceLabels: new Map(
      response.resources.map((x) => [x.code, labelOr(x.label, x.code)])
    ),
  }
}

/** Le libellé d'une action ; son code tant que le catalogue manque, ou s'il ne la connaît pas. */
export const actionLabel = (
  catalogue: Catalogue | undefined,
  code: string
): string => catalogue?.actionsByCode.get(code)?.label ?? code

export const presetLabel = (
  catalogue: Catalogue | undefined,
  id: PresetId
): string => catalogue?.presetsById.get(id)?.label ?? id

export const moduleLabel = (catalogue: Catalogue, module: string): string =>
  catalogue.moduleLabels.get(module) ?? module

export const resourceLabel = (catalogue: Catalogue, resource: string): string =>
  catalogue.resourceLabels.get(resource) ?? resource
